import { previewSearch } from "@/api/endpoints/preview";
import { createSearch } from "@/api/endpoints/searches";
import Layout from "@/containers/Layout";
import { MaxAlertLimitReached } from "@/containers/MaxAlertLimitReached";
import QueryBuilder, {
  QueryBuilderSubmitButton,
} from "@/containers/QueryBuilderNew";
import QueryBuilderErrors from "@/containers/QueryBuilderNew/partials/Errors";
import PreviewMentionList from "@/containers/QueryBuilderNew/partials/PreviewMentionList";
import {
  prepareQueryForCreate,
  prepareQueryForPreview,
} from "@/containers/QueryBuilderNew/requestUtils";
import { QueryBuilderForm } from "@/containers/QueryBuilderNew/types";
import { ROUTES } from "@/routes_new";
import { formatDateForApi } from "@/utils";
import AllEarsTitle from "@components/AllEarsTitle";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { Button } from "@components/ui/button";
import Error from "@components/ui/error";
import { Separator } from "@components/ui/separator";
import { SidebarTrigger } from "@components/ui/sidebar";
import { Skeleton } from "@components/ui/skeleton";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EyeIcon, RefreshCwIcon } from "lucide-react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

export const CreateAlertView = () => {
  const { t } = useLingui();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();
  const history = useHistory();

  // Server state
  const queryClient = useQueryClient();

  const {
    isPending: isPreviewPending,
    mutate: mutatePreview,
    error: previewError,
    data: previewData,
  } = useMutation({
    mutationFn: previewSearch,
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(t`Lyckades inte ladda sökresultaten`);
      }
    },
    onError: () => {
      toast.error(t`Lyckades inte ladda sökresultaten`);
    },
  });

  const {
    isPending: isCreatePending,
    mutate: mutateCreate,
    data: createData,
  } = useMutation({
    mutationFn: createSearch,
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t`Bevakningen skapades`);

        queryClient.invalidateQueries({
          queryKey: [orgUuid, "searches"],
        });

        if (res?.ok && res.data?.uuid) {
          history.push(
            generatePath(ROUTES.MENTION_DETAIL_ORGID, {
              uuid: res.data.uuid,
              orgUuid,
            })
          );
          window.location.reload();
        }
      } else {
        toast.error(t`Lyckades inte skapa bevakningen`);
      }
    },
    onError: () => {
      toast.error(t`Lyckades inte skapa bevakningen`);
    },
  });

  // Handlers
  function handlePreview(values: QueryBuilderForm) {
    mutatePreview({
      end_date: values.endDate ? formatDateForApi(values.endDate) : null,
      start_date: formatDateForApi(values.startDate),
      industry: "",
      queries: values.queries.map(prepareQueryForPreview),
    });
  }

  function handleCreate(values: QueryBuilderForm) {
    mutateCreate({
      title: values.title,
      start_date: formatDateForApi(values.startDate),
      end_date: values.endDate ? formatDateForApi(values.endDate) : null,
      search_type: values.type,
      queries: values.queries.map(prepareQueryForCreate),
      information_need: values.informationNeed ?? "",
    });
  }

  function handleSubmit(
    values: QueryBuilderForm,
    submitterName?: QueryBuilderSubmitButton
  ) {
    if (submitterName === QueryBuilderSubmitButton.Create) {
      return handleCreate(values);
    }

    return handlePreview(values);
  }

  return (
    <>
      <AllEarsTitle title={t`Skapa ny bevakning`} />
      <Layout>
        <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
          <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-7xl">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mx-2 data-[orientation=vertical]:h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Trans>Bevakningar</Trans>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <Trans>Ny</Trans>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="ml-auto flex items-center gap-2.5">
              <Button
                form="form-qb"
                id="form-qb-preview"
                name="form-qb-preview"
                disabled={isPreviewPending}
                variant={previewData?.ok ? "outline" : "default"}
              >
                {previewData?.ok ? <RefreshCwIcon /> : <EyeIcon />}
                <Trans>Förhandsvisa</Trans>
              </Button>

              {previewData?.ok ? (
                <Button
                  form="form-qb"
                  id="form-qb-create"
                  name="form-qb-create"
                  disabled={isCreatePending}
                >
                  <Trans>Skapa bevakning</Trans>
                </Button>
              ) : null}
            </div>
          </div>
        </header>

        <div className="border-b">
          <div className="w-full p-4 lg:p-6 max-w-7xl">
            <h1 className="text-2xl font-bold">
              <Trans>Skapa bevakning</Trans>
            </h1>
          </div>
        </div>

        <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-4">
          <QueryBuilder
            onSubmit={handleSubmit}
            informationNeedError={
              createData && !createData.ok
                ? createData.data?.information_need?.[0]
                : undefined
            }
          />

          {previewData ? (
            previewData.ok ? (
              <PreviewMentionList
                meta={previewData.data.meta}
                mentions={previewData.data.results}
              />
            ) : (
              <div>
                <Error
                  heading={t`Åh nej!`}
                  description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
                />

                {previewData.data && typeof previewData.data === "object" && (
                  <QueryBuilderErrors errors={previewData.data} />
                )}
              </div>
            )
          ) : isPreviewPending ? (
            <>
              <Skeleton className="w-full h-64" />
              <Skeleton className="w-full h-64" />
              <Skeleton className="w-full h-64" />
            </>
          ) : previewError ? (
            <Error
              heading={t`Åh nej!`}
              description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
              error={previewError}
            />
          ) : null}

          <MaxAlertLimitReached />
        </div>
      </Layout>
    </>
  );
};
